import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, H2, H3 } from '@liveauctioneers/hammer-ui-core/text';
import { ReactElement, useCallback } from 'react';

import type { ItemSummary } from '@/types/item/ItemSummary';

import PageTitle from '@/components/PageTitle/PageTitle';
import { Catalog } from '@/types/Catalog';
import { shortFormatDate } from '@/utils/dates';
import { getImageUrl, getItemUrlFromSlug } from '@/utils/urls';

import ReviewPageHeaderMeta from './ReviewPageHeaderMeta';

type Props = {
    baseUrl: string;
    catalog: Catalog;
    handleNotReceivedItemsClicked: () => void;
    handleReceivedItemsClicked: () => void;
    items: ItemSummary[];
    receivedItems: boolean;
    selectedReceivedItems: boolean;
};

export const ReviewPageHeader = ({
    baseUrl,
    catalog,
    handleNotReceivedItemsClicked,
    handleReceivedItemsClicked,
    items,
    receivedItems,
    selectedReceivedItems,
}: Props) => {
    const { formatMessage } = useIntl();
    const mapImages = useCallback((item: ItemSummary): ReactElement => {
        const { catalogId, imageVersion, itemId, photos, sellerId, slugWithLocation, title } = item;
        const imageOptions: any = {
            catalogId,
            height: 240,
            imageNumber: photos?.[0],
            imageVersion,
            itemId,
            sellerId,
        };

        const url = getImageUrl(imageOptions);
        const itemUrl = getItemUrlFromSlug(itemId, slugWithLocation);
        return (
            <div
                className="relative mr-xs h-[120px] w-[120px] border border-border-secondary"
                key={itemId}
            >
                <Link
                    href={itemUrl}
                    key={itemId}
                    rel="noopener"
                    target="_blank"
                >
                    <img
                        alt={title}
                        className="absolute top-0 right-0 bottom-0 left-0 m-auto h-auto max-h-full w-auto max-w-full"
                        key={itemId}
                        src={url}
                    />
                </Link>
            </div>
        );
    }, []);

    const { saleStartTs, sellerName: name, title } = catalog;

    const startDate = shortFormatDate(saleStartTs);
    return (
        <div>
            <ReviewPageHeaderMeta baseUrl={baseUrl} />
            <PageTitle
                className="text-center"
                title={<FormattedMessage id="review_page.review_your_auction" />}
            />
            <div>
                <H2>{name}</H2>
                <BodyPrimary>{title}</BodyPrimary>
                <br />
                {startDate}
            </div>
            <div className="mt-sm flex">{items.map(mapImages)}</div>
            <div className="mt-xl">
                <H3>
                    {items.length > 1 ? (
                        <FormattedMessage id="review_page.received_items" />
                    ) : (
                        <FormattedMessage id="review_page.received_item" />
                    )}
                </H3>
                <div className="mt-sm flex">
                    <Button
                        className="mr-md inline-flex"
                        onClick={handleReceivedItemsClicked}
                        variant={selectedReceivedItems && receivedItems ? 'filled' : 'outlined'}
                    >
                        {formatMessage({ id: 'review_page.yes' })}
                    </Button>
                    <Button
                        className="inline-flex"
                        onClick={handleNotReceivedItemsClicked}
                        variant={selectedReceivedItems && !receivedItems ? 'filled' : 'outlined'}
                    >
                        {formatMessage({ id: 'review_page.no' })}
                    </Button>
                </div>
            </div>
        </div>
    );
};
